'use client';

import { useState } from 'react';
import AppLogo from '@/components/ui/AppLogo';
import { createClient } from '@/lib/supabase/client';

export default function ContatoSection() {
  const [formData, setFormData] = useState({
    nome: '',
    telefone: '',
    email: '',
    perfil: '',
    capital: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.nome.trim()) {
      setError('Por favor, informe seu nome completo.');
      return;
    }
    if (!formData.telefone.trim()) {
      setError('Por favor, informe seu WhatsApp.');
      return;
    }

    setLoading(true);
    try {
      const supabase = createClient();
      const { error: supabaseError } = await supabase
        .from('contact_leads')
        .insert({
          nome: formData.nome.trim(),
          telefone: formData.telefone.trim(),
          email: formData.email.trim() || null,
          perfil: formData.perfil || null,
          capital: formData.capital || null,
        });

      if (supabaseError) {
        setError('Ocorreu um erro ao enviar sua solicitação. Tente novamente.');
        return;
      }

      // Send admin email notification (fire-and-forget)
      try {
        const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
        const leadData = {
          nome: formData.nome.trim(),
          telefone: formData.telefone.trim(),
          email: formData.email.trim() || null,
          perfil: formData.perfil || null,
          capital: formData.capital || null,
        };

        // Notify admin
        await fetch(`${supabaseUrl}/functions/v1/send-admin-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'new_lead',
            data: leadData,
          }),
        });

        // Send confirmation email to the lead (only if email was provided)
        if (leadData.email) {
          await fetch(`${supabaseUrl}/functions/v1/send-admin-email`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type: 'lead_confirmation',
              data: leadData,
            }),
          });
        }
      } catch {
        // Email notification failure should not block the user flow
      }

      setSubmitted(true);
    } catch {
      setError('Ocorreu um erro inesperado. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const whatsappMsg = encodeURIComponent(
    'Olá! Vim pelo site VS Cardoso Studios e gostaria de receber uma análise personalizada de investimento em Curitiba.'
  );

  return (
    <section id="contato" className="py-28 px-6 bg-bg relative overflow-hidden">
      {/* Atmospheric glows */}
      <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-primary/20 blur-[140px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-accent/8 blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-start">

          {/* Left: VS Cardoso brand + strategy */}
          <div className="reveal-left">
            <span className="block text-[10px] uppercase tracking-[0.3em] text-accent font-medium mb-6">
              VS Cardoso — Estratégia Antes da Venda
            </span>

            <h2 className="font-display text-5xl md:text-6xl leading-[0.92] tracking-tight font-semibold mb-6">
              Análise antes<br />
              <span className="italic font-light text-gold-gradient">de qualquer decisão.</span>
            </h2>

            <p className="text-fg/55 text-base leading-relaxed font-light mb-8">
              Diferente de quem só vende imóvel, a VS Cardoso faz o estudo completo antes de apresentar qualquer opção. Você recebe projeções reais de rentabilidade, análise de localização e comparativo de potencial de valorização.
            </p>

            {/* Differentiators */}
            <div className="space-y-4 mb-10">
              {[
                { icon: '📊', title: 'Projeção de Rentabilidade', desc: 'Simulação personalizada com yield esperado, taxa de vacância histórica e comparativo de mercado.' },
                { icon: '📍', title: 'Análise de Localização', desc: 'Mapeamento de valorização por bairro, infraestrutura, demanda e potencial de expansão urbana.' },
                { icon: '🤝', title: 'Acompanhamento Pós-Compra', desc: 'Suporte na gestão do imóvel, indicação de administradoras e estratégia de saída quando necessário.' },
              ].map((d, i) => (
                <div key={i} className="flex items-start gap-4 p-5 border border-fg/8 rounded-sm hover:border-accent/25 transition-colors">
                  <span className="text-2xl shrink-0">{d.icon}</span>
                  <div>
                    <h4 className="font-semibold text-fg text-sm mb-1">{d.title}</h4>
                    <p className="text-fg/50 text-sm font-light leading-relaxed">{d.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Logo area */}
            <div className="flex items-center gap-4">
              <AppLogo
                src="/assets/images/IMG-20260218-WA0010-1771430982362.jpg"
                size={52}
                className="rounded-sm"
              />
              <div>
                <p className="font-semibold text-fg text-sm">VS Cardoso Studios</p>
                <p className="text-fg/40 text-xs font-light">Especialistas em Investimento Imobiliário · Curitiba</p>
              </div>
            </div>
          </div>

          {/* Right: Form + WhatsApp */}
          <div className="reveal-right">
            <div className="glass-card rounded-sm p-8">
              <h3 className="font-display text-2xl font-semibold mb-2">Receba sua análise gratuita</h3>
              <p className="text-fg/50 text-sm font-light mb-8">
                Preencha abaixo e um especialista da VS Cardoso entra em contato em até 24 horas com um estudo personalizado.
              </p>

              {!submitted ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-fg/50 mb-2 font-medium">
                      Nome completo *
                    </label>
                    <input
                      type="text"
                      name="nome"
                      required
                      value={formData.nome}
                      onChange={handleChange}
                      placeholder="Seu nome"
                      className="w-full bg-fg/5 border border-fg/10 rounded-sm px-4 py-3 text-sm text-fg placeholder-fg/30 transition-all"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] uppercase tracking-widest text-fg/50 mb-2 font-medium">
                        WhatsApp *
                      </label>
                      <input
                        type="tel"
                        name="telefone"
                        required
                        value={formData.telefone}
                        onChange={handleChange}
                        placeholder="(41) 99999-9999"
                        className="w-full bg-fg/5 border border-fg/10 rounded-sm px-4 py-3 text-sm text-fg placeholder-fg/30 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-widest text-fg/50 mb-2 font-medium">
                        E-mail
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="seu@email.com"
                        className="w-full bg-fg/5 border border-fg/10 rounded-sm px-4 py-3 text-sm text-fg placeholder-fg/30 transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-fg/50 mb-2 font-medium">
                      Perfil de investidor
                    </label>
                    <select
                      name="perfil"
                      value={formData.perfil}
                      onChange={handleChange}
                      className="w-full bg-fg/5 border border-fg/10 rounded-sm px-4 py-3 text-sm text-fg transition-all appearance-none"
                    >
                      <option value="" className="bg-bg">Selecione seu perfil</option>
                      <option value="iniciante" className="bg-bg">Investidor Iniciante</option>
                      <option value="diversificador" className="bg-bg">Diversificador de Portfólio</option>
                      <option value="renda" className="bg-bg">Gerador de Renda Passiva</option>
                      <option value="estrategico" className="bg-bg">Investidor Estratégico</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-fg/50 mb-2 font-medium">
                      Capital disponível para investimento
                    </label>
                    <select
                      name="capital"
                      value={formData.capital}
                      onChange={handleChange}
                      className="w-full bg-fg/5 border border-fg/10 rounded-sm px-4 py-3 text-sm text-fg transition-all appearance-none"
                    >
                      <option value="" className="bg-bg">Selecione a faixa</option>
                      <option value="ate300k" className="bg-bg">Até R$ 300.000</option>
                      <option value="300-600k" className="bg-bg">R$ 300k – R$ 600k</option>
                      <option value="600k-1m" className="bg-bg">R$ 600k – R$ 1M</option>
                      <option value="acima1m" className="bg-bg">Acima de R$ 1M</option>
                    </select>
                  </div>

                  {error && (
                    <div className="px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-sm">
                      <p className="text-red-400 text-xs font-medium">{error}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-4 bg-accent text-bg font-semibold text-sm uppercase tracking-widest rounded-sm hover:bg-accent-light transition-colors duration-200 mt-2 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Enviando...
                      </>
                    ) : (
                      'Quero Minha Análise Gratuita'
                    )}
                  </button>
                </form>
              ) : (
                <div className="text-center py-12">
                  <div className="text-4xl mb-4">✅</div>
                  <h4 className="font-display text-2xl font-semibold mb-2">Recebemos sua solicitação!</h4>
                  <p className="text-fg/55 text-sm font-light">
                    Um especialista da VS Cardoso entrará em contato em até 24 horas com sua análise personalizada.
                  </p>
                </div>
              )}

              {/* Divider */}
              <div className="flex items-center gap-4 my-6">
                <div className="flex-1 h-px bg-fg/10" />
                <span className="text-[10px] uppercase tracking-widest text-fg/30">ou</span>
                <div className="flex-1 h-px bg-fg/10" />
              </div>

              {/* WhatsApp CTA */}
              <a
                href={`https://wa.me/5541999999999?text=${whatsappMsg}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 w-full py-4 border border-fg/15 rounded-sm text-sm font-semibold text-fg/80 hover:border-accent/40 hover:text-fg transition-all duration-200 group"
              >
                <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                Conversar pelo WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}